<script>
import { Salutation } from '@/enums/Salutation'
import { fieldValidationsMixin } from '@/mixins/fieldValidationsMixin'
import { validationMixin } from 'vuelidate'
import {
  required,
  minLength,
  sameAs,
  requiredIf,
  email
} from 'vuelidate/lib/validators'
export default {
  mixins: [validationMixin, fieldValidationsMixin],
  props: {
    value: {
      type: Object
    }
  },
  validations: {
    value: {
      email: { required, email },
      salutation: { required },
      firstName: { required },
      lastName: { required },
      address: { required },
      city: { required },
      zipCode: {
        required,
        onlyDigits(val) {
          const reg = /^\d+$/
          return reg.test(val)
        },
        correctLength(val) {
          if (!val) return false
          return val.length === 5
        }
      },
      taxId: {
        required: requiredIf(function () {
          return this.value.taxId !== null && this.value.taxId.length > 0
        }),
        isGermanTaxId(val) {
          if (this.value.taxId !== null && this.value.taxId.length > 0)
            return /^(DE)+[0-9]{9}$/.test(val)
          else return true
        },
        hasGermanCountryCode(val) {
          if (this.value.taxId !== null && this.value.taxId.length > 0)
            return /^(DE)/.test(val)
          else return true
        },
        hasUniqueNumbers(val) {
          if (this.value.taxId !== null && this.value.taxId.length > 0)
            return /[0-9]{9}$/.test(val)
          else return true
        }
      },
      password: {
        required,
        minLength: minLength(6),
        hasCapital(pwd) {
          return /[A-Z]/.test(pwd)
        },
        hasNum(pwd) {
          return /[0-9]/.test(pwd)
        },
        hasSpecialChar(pwd) {
          return /\W|_/.test(pwd)
        }
      },
      confirmPassword: {
        required,
        minLength: minLength(6),
        sameAs: sameAs(function () {
          return this.value.password
        })
      }
    }
  },
  data() {
    return {
      loading: false,
      salutations: [
        {
          value: Salutation.Sir,
          text: this.$t('form.label.mr')
        },
        {
          value: Salutation.Madam,
          text: this.$t('form.label.mrs')
        },
        {
          value: Salutation.Company,
          text: this.$t('form.label.company')
        }
      ],
      showPassword: false,
      showPasswordRepeat: false
    }
  },
  methods: {
    getFirstNameLabel() {
      if (this.value.salutation === Salutation.Company) {
        return this.$t('form.label.company.name')
      } else {
        return this.$t('form.label.first.name')
      }
    },
    getLastNameLabel() {
      if (this.value.salutation === Salutation.Company) {
        return this.$t('form.label.company.contact')
      } else {
        return this.$t('form.label.last.name')
      }
    }
  }
}
</script>
<template>
  <div>
    <v-row align="center">
      <v-col cols="12" md="6" class="pb-0">
        <v-select
          id="migration_salutation"
          v-model="value.salutation"
          :items="salutations"
          item-value="value"
          item-text="text"
          :label="$t('form.label.salutation')"
          :error-messages="fieldNeccessary($v.value.salutation, $v)"
          outlined
          dense
          required
        ></v-select>
      </v-col>
    </v-row>
    <v-row dense align="center">
      <v-col cols="12" md="6">
        <v-text-field
          id="migration_firstName"
          v-model="value.firstName"
          :label="getFirstNameLabel()"
          :error-messages="fieldNeccessary($v.value.firstName, $v)"
          outlined
          dense
          required
          @input="$v.value.firstName.$touch()"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          id="migration_lastName"
          v-model="value.lastName"
          :label="getLastNameLabel()"
          :error-messages="fieldNeccessary($v.value.lastName, $v)"
          outlined
          dense
          required
          @input="$v.value.lastName.$touch()"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          id="migration_address"
          v-model="value.address"
          :label="$t('form.label.address')"
          :error-messages="fieldNeccessary($v.value.address, $v)"
          outlined
          dense
          required
          @input="$v.value.address.$touch()"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          id="migration_addressAddition"
          v-model="value.addressAddition"
          :label="$t('form.label.address.extension')"
          outlined
          dense
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          id="migration_zipCode"
          v-model="value.zipCode"
          :label="$t('form.label.zip.code')"
          :error-messages="[
            ...fieldNeccessary($v.value.zipCode, $v),
            ...zipCodeErrors($v.value.zipCode, $v)
          ]"
          outlined
          dense
          required
          @input="$v.value.zipCode.$touch()"
        />
      </v-col>
      <v-col cols="12" md="8">
        <v-text-field
          id="migration_city"
          v-model="value.city"
          :label="$t('form.label.city')"
          :error-messages="fieldNeccessary($v.value.city, $v)"
          outlined
          dense
          required
          @input="$v.value.city.$touch()"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-row dense>
          <v-col cols="auto"
          ><v-tooltip top>
            <template #activator="{ on }">
              <v-icon color="primary" v-on="on"
              >mdi-alert-circle-outline</v-icon>
            </template>
            <span>{{ $t('form.label.tooltip.country') }}</span>
          </v-tooltip>
          </v-col>
          <v-col>
            <v-text-field
              id="migration_country"
              v-model="value.country"
              :label="$t('form.label.country')"
              outlined
              dense
              filled
              disabled
              @input="changeCountry()">
            </v-text-field>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="8">
        <v-text-field
          id="migration_phoneNumber"
          v-model="value.phoneNumber"
          :label="$t('form.label.phone')"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="migration_email"
          v-model="value.email"
          :label="$t('form.label.email')"
          :error-messages="[
            ...fieldNeccessary($v.value.email, $v),
            ...emailInvalid($v.value.email, $v)
          ]"
          outlined
          dense
          required
          @input="$v.value.email.$touch()">
        </v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="migration_taxId"
          v-model="value.taxId"
          :label="$t('form.label.tax.id')"
          :error-messages="[
            ...fieldNeccessary($v.value.taxId, $v),
            ...taxIdErrors($v.value.taxId, $v)
          ]"
          outlined
          dense
          required
          @input="$v.value.taxId.$touch()"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          id="migration_password"
          v-model="value.password"
          :label="$t('form.label.password')"
          :error-messages="[
            ...fieldNeccessary($v.value.password, $v),
            ...password($v.value.password, $v)
          ]"
          :type="showPassword ? 'text' : 'password'"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          persistent-hint
          counter
          outlined
          dense
          required
          @input="$v.value.password.$touch()"
          @click:append="showPassword = !showPassword"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          id="migration_password_repeat"
          v-model="value.confirmPassword"
          :label="$t('component.profile.accountManagement.repeat.password')"
          :error-messages="[
            ...fieldNeccessary($v.value.confirmPassword, $v),
            ...passwordRepeat($v.value.confirmPassword, $v)
          ]"
          :type="showPasswordRepeat ? 'text' : 'password'"
          :append-icon="showPasswordRepeat ? 'mdi-eye' : 'mdi-eye-off'"
          persistent-hint
          counter
          outlined
          dense
          required
          @input="$v.value.confirmPassword.$touch()"
          @click:append="showPasswordRepeat = !showPasswordRepeat"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>
